/**
 * Phase 7B — Modal for SPOC to raise a budget-increase request on a CC.
 *
 * Captures `amount` + `reason` and POSTs to /budget-request. The parent CC
 * view refetches on success and renders the new request via
 * CCBudgetRequestApprovalSheet.
 */

import { FunctionComponent, useState } from "react";
import { fetchPost } from "../../../../service/api_client";
import {
  ToastType,
  useToast,
} from "../../../../context_providers/toast/toast_ctx";

interface Props {
  cc_id: string;
  cc_name: string;
  /** Called after a successful submission so the parent can refetch the CC. */
  onSubmitted: () => void;
  onClose: () => void;
}

const CCBudgetRequestModal: FunctionComponent<Props> = ({
  cc_id,
  cc_name,
  onSubmitted,
  onClose,
}) => {
  const { showToast } = useToast();
  const [amount, setAmount] = useState<number | "">("");
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    if (amount === "" || Number(amount) <= 0) {
      showToast({
        type: ToastType.error,
        text: "Please enter a positive amount.",
      });
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetchPost(
        `${process.env.REACT_APP_BACKEND_BASE_URL}/api/v3/erp/inventory/cost_center/${cc_id}/budget-request`,
        { amount: Number(amount), reason: reason.trim() || undefined },
      );
      if (res.success) {
        showToast({ type: ToastType.success, text: res.message });
        onSubmitted();
        onClose();
      } else {
        showToast({ type: ToastType.error, text: res.error });
      }
    } catch (e: any) {
      showToast({ type: ToastType.error, text: e.message });
    }
    setSubmitting(false);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-3">
      <div className="bg-white rounded-md shadow-lg w-full max-w-md p-5">
        <h2 className="text-base font-semibold text-gray-900">
          Raise Budget Increase
        </h2>
        <p className="text-xs text-gray-500 mt-0.5">
          For Cost Center <b>{cc_name}</b>. The request will walk BU Head &rarr;
          Dept Head &rarr; Finance (tier by amount).
        </p>

        <div className="mt-4 flex flex-col gap-3">
          <div>
            <label className="block text-[11px] font-semibold text-gray-700">
              Amount (₹) <span className="text-rose-600">*</span>
            </label>
            <input
              type="number"
              min={1}
              data-testid="cc-budget-request-amount"
              value={amount}
              onChange={(e) =>
                setAmount(e.target.value === "" ? "" : Number(e.target.value))
              }
              placeholder="e.g. 50000"
              className="mt-1 w-full border border-gray-300 rounded-md px-2 py-1 text-sm"
            />
            <p className="text-[10px] text-gray-500 mt-1">
              Finance tier auto-routed:{" "}
              {amount && Number(amount) > 0
                ? Number(amount) <= 25000
                  ? "L1 (≤₹25k)"
                  : Number(amount) <= 200000
                    ? "L2 (≤₹2L)"
                    : "L3 (>₹2L)"
                : "—"}
            </p>
          </div>
          <div>
            <label className="block text-[11px] font-semibold text-gray-700">
              Reason (optional)
            </label>
            <textarea
              data-testid="cc-budget-request-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
              placeholder="Brief justification for the increase…"
              className="mt-1 w-full border border-gray-300 rounded-md px-2 py-1 text-sm"
            />
          </div>
        </div>

        <div className="mt-5 flex justify-end gap-2">
          <button
            onClick={onClose}
            disabled={submitting}
            className="border border-gray-300 rounded-md px-3 py-1.5 text-sm"
          >
            Cancel
          </button>
          <button
            onClick={submit}
            disabled={submitting}
            data-testid="cc-budget-request-submit"
            className="bg-myPrimaryColor text-white rounded-md px-4 py-1.5 text-sm"
          >
            {submitting ? "Submitting…" : "Submit Request"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CCBudgetRequestModal;
